import { withRouter } from "react-router-dom";
import * as _ from "lodash";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import MainContent from "../../boxes/MainContent";
import PageHeader from "../../boxes/PageHeader";
import PageBody from "../../boxes/PageBody";
import { useTranslation } from "react-i18next";
import LoadingScreen from "../../LoadingScreen";
import { useEffect, useState } from "react";
import { careBalanceLearnMoreUrl } from "../constants/careBalanceLearnMoreUrl";
import userService from "../../../services/userService";
import useCareBalance from "../hooks/useCareBalance";
import careBalanceService from "../../../services/careBalanceService";
import { formatAsMoney } from "../utils/format-as-money";
import { extractErrorMessage } from '../utils/extract-error-message';

const infoCircleBlue = "/img/care-balance/info-circle-blue.svg";

const formGroupTitleStyle = {
  fontWeight: 700,
  fontSize: "1.859rem",
  color: "#094063",
};

const fieldSetStyle = { display: "flex", flexDirection: "column" };

const labelStyle = {
  fontWeight: 500,
  fontSize: "1.291rem",
  lineHeight: "24px",
  color: "#4F4F4F",
};

const inputStyle = {
  borderRadius: "4px",
  paddingBlock: "11px",
  paddingInline: "13px",
  border: "1px solid #E5E5E5",
  color: "#4F4F4F",
  fontSize: "1.291rem",
};

const buttonStyle = {
  width: "228.5px",
  maxWidth: "100%",
  borderRadius: "4px",
  border: "1px solid #094063",
  paddingBlock: "11.5px",
  paddingInline: "10px",
  fontWeight: 800,
  fontSize: "1.413rem",
};

const errorStyle = {
  color: "red",
  fontSize: "1.2rem",
  marginTop: "4px",
};

function RequestRefundContainer(props) {
  const { user } = props;
  const { wallet, loading } = useCareBalance({ hmoId: user?.hmoId });
  const [banks, setBanks] = useState([]);

  const validationSchema = yup.object().shape({
    phoneNumber: yup.string().required("Phone number is required"),
    bankCode: yup.string().required("Please select a bank"),
    accountNumber: yup
      .string()
      .required("Account number is required")
      .matches(/^[0-9]{10}$/, "Account number must be 10 digits"),
    accountName: yup.string().required("Account name is required"),
    amount: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .max(
        wallet?.balance?.available,
        `Amount exceeds available balance of ${formatAsMoney({
          value: wallet?.balance?.available,
          currency: wallet?.currency,
        })}`
      )
      .required("Amount is required")
      .positive("Amount must be a positive number")
      .typeError("Please enter a valid amount"),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    watch,
    setValue,
    setError,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onTouched",
    defaultValues: {
      phoneNumber: user?.phoneNumber || "",
      bankCode: "",
      accountNumber: "",
      accountName: "",
      amount: "",
    },
  });

  const { t } = useTranslation("careBalance");

  useEffect(() => {
    userService.getBanks().then((banks) => setBanks(banks));
  }, []);

  const onSubmit = async (data) => {
    const selectedBank = banks.find(
      (bank) => bank.sort_code === data.bankCode
    );

    const payload = {
      ...data,
      bankCode: data.bankCode,
      walletId: wallet?.id,
      hmoId: user?.hmoId,
      enrolleeName: `${user?.firstName} ${user?.lastName}`,
      bankName: selectedBank?.name || "",
      emailAddress: user?.emailAddress,
      currency: wallet?.currency,
      amount: Number(data.amount),
    };

    return careBalanceService
      .withdraw(payload)
      .then(() => {
        props.history.push("/request-refund-submitted");
      })
      .catch((err) => {
        console.error(err);
        setError("root.serverError", {
          type: "manual",
          message:
            extractErrorMessage(err) ||
            "There was an issue submitting your request. Please try again.",
        });
      });
  };

  if (loading || isSubmitting || _.isEmpty(user)) {
    return <LoadingScreen />;
  }

  return (
    <>
      <MainContent>
        <PageHeader title={t("requestRefundTitle")} />

        <PageBody
          style={{
            gap: "16px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Refund alert */}
          <div
            style={{
              backgroundColor: "#F0F8FF",
              borderRadius: "16px",
              padding: "16px",
              gap: "8px",
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            <img
              src={infoCircleBlue}
              style={{
                width: "24px",
                height: "24px",
              }}
            />

            <div>
              <p
                style={{
                  fontWeight: 500,
                  lineHeight: "24px",
                  fontSize: "1.4rem",
                  color: "#0D5889",
                }}
              >
                {t("requestRefundPleaseNote")}
              </p>
              <ul
                style={{
                  listStyleType: "disc",
                  letterSpacing: "0px",
                  fontSize: "1.4rem",
                  color: "#137CC1",
                  marginBottom: "8px",
                }}
              >
                <li>{t("requestRefundFirstInfo")}</li>
                <li> {t("requestRefundSecondInfo")}</li>
              </ul>

              <a
                target="_blank"
                href={careBalanceLearnMoreUrl}
                style={{ fontWeight: 500, fontSize: "1.4rem" }}
              >
                {t("learnMore")}
              </a>
            </div>
          </div>

          {/* Description text */}
          <p
            style={{
              fontSize: "1.291rem",
              color: "#4F4F4F",
            }}
          >
            {t("requestRefundSubtitle")}
          </p>

          <form
            style={{
              gap: "16px",
              display: "flex",
              flexDirection: "column",
            }}
            onSubmit={handleSubmit(onSubmit)}
          >
            {errors.root?.serverError && !isSubmitSuccessful && (
              <p style={{ ...errorStyle}}>
                {errors.root.serverError.message}
              </p>
            )}

            <div
              className="dashboard-card"
              style={{
                display: "flex",
                flexDirection: "column",
                rowGap: "16px",
              }}
            >
              <h3 style={formGroupTitleStyle}>
                {t("requestRefundPersonalDetails")}
              </h3>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "16px",
                }}
              >
                <fieldset style={fieldSetStyle}>
                  <label htmlFor="firstName" style={labelStyle}>
                    {t("requestRefundFirstNameField")}
                  </label>
                  <input
                    id="firstName"
                    style={inputStyle}
                    value={user?.firstName}
                    readOnly={true}
                  />
                </fieldset>

                <fieldset style={fieldSetStyle}>
                  <label htmlFor="lastName" style={labelStyle}>
                    {t("requestRefundLastNameField")}
                  </label>
                  <input
                    id="lastName"
                    style={inputStyle}
                    value={user?.lastName}
                    readOnly={true}
                  />
                </fieldset>

                <fieldset style={fieldSetStyle}>
                  <label htmlFor="email" style={labelStyle}>
                    {t("requestRefundEmailField")}
                  </label>
                  <input
                    id="email"
                    type="email"
                    style={inputStyle}
                    value={user?.emailAddress}
                    readOnly={true}
                  />
                </fieldset>

                <fieldset style={fieldSetStyle}>
                  <label htmlFor="phoneNumber" style={labelStyle}>
                    {t("requestRefundPhoneNumberField")}
                  </label>
                  <input
                    id="phoneNumber"
                    style={inputStyle}
                    {...register("phoneNumber")}
                  />
                  {errors.phoneNumber && (
                    <p style={errorStyle}>{errors.phoneNumber.message}</p>
                  )}
                </fieldset>
              </div>
            </div>

            <div
              className="dashboard-card"
              style={{
                display: "flex",
                flexDirection: "column",
                rowGap: "16px",
              }}
            >
              <h3 style={formGroupTitleStyle}>
                {t("requestRefundBankDetails")}
              </h3>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "16px",
                }}
              >
                <fieldset style={{ ...fieldSetStyle, gridColumn: "span 2" }}>
                  <label htmlFor="bank" style={labelStyle}>
                    {t("requestRefundBankField")}
                  </label>
                  <select
                    id="bank"
                    style={inputStyle}
                    {...register("bankCode")}
                  >
                    <option value=""></option>
                    {banks.map((bank) => (
                      <option key={bank.sort_code} value={bank.sort_code}>
                        {bank.name}
                      </option>
                    ))}
                  </select>
                  {errors.bankCode && (
                    <p style={errorStyle}>{errors.bankCode.message}</p>
                  )}
                </fieldset>

                <fieldset style={fieldSetStyle}>
                  <label htmlFor="accountNumber" style={labelStyle}>
                    {t("requestRefundAccountNumberField")}
                  </label>
                  <input
                    id="accountNumber"
                    style={inputStyle}
                    type={"numeric"}
                    {...register("accountNumber")}
                  />
                  {errors.accountNumber && (
                    <p style={errorStyle}>{errors.accountNumber.message}</p>
                  )}
                </fieldset>

                <fieldset style={fieldSetStyle}>
                  <label htmlFor="accountName" style={labelStyle}>
                    {t("requestRefundAccountNameField")}
                  </label>
                  <input
                    id="accountName"
                    style={inputStyle}
                    {...register("accountName")}
                  />
                  {errors.accountName && (
                    <p style={errorStyle}>{errors.accountName.message}</p>
                  )}
                </fieldset>

                <fieldset style={{ ...fieldSetStyle, gridColumn: "span 2" }}>
                  <label htmlFor="amount" style={labelStyle}>
                    {t("requestRefundAmountField")}
                  </label>
                  <input
                    id="amount"
                    style={inputStyle}
                    type="number"
                    {...register("amount")}
                  />
                  {errors.amount && (
                    <p style={errorStyle}>{errors.amount.message}</p>
                  )}
                </fieldset>
              </div>
            </div>

            <div style={{ display: "flex", gap: "16px" }}>
              <button
                type="button"
                style={{ ...buttonStyle, color: "#094063" }}
                onClick={() => props.history.goBack()}
              >
                {t("requestRefundCancelButton")}
              </button>
              <button
                style={{
                  ...buttonStyle,
                  backgroundColor: "#094063",
                  color: "#ffffff",
                }}
                disabled={isSubmitting}
              >
                {t("requestRefundSubmitButton")}
              </button>
            </div>
          </form>
        </PageBody>
      </MainContent>
    </>
  );
}

export default withRouter(RequestRefundContainer);
